import React from "react";
import _ from "lodash";

import { connect } from 'react-redux';
import withStyles from "@material-ui/core/styles/withStyles";
import Grid from "@material-ui/core/Grid";

import PrimaryButton from "../../../../../../../components/Buttons/PrimaryButton";
import BaseDialog from "../../../../../../../components/BaseDialog/BaseDialog";
import {useAssetsLiabilitiesFromRiskProfile} from "../../../../hooks/useAssetsLiabilities";
import {getAssetsListData, getUID} from "../../../../../../RiskProfiling/utils";
import {
  ASSET_MODAL_TYPE_NEW,
  GROUPED_ASSET_AND_LIABILITY_CATEGORIES,
  OTHER_FINANCIAL_CATEGORY
} from "../../../../../constants";
import SelectAssetCard from "./components/SelectAssetCard";
import CategoryHeader from "../../../CategoryHeader";

import styles from "./styles";

const MAPPING = {
  'Bargeld': {
    asset_type: ASSET_MODAL_TYPE_NEW.LIQUID,
    sub_category: 1  // Bargeld
  },
  'Investments & Depots': {
    asset_type: ASSET_MODAL_TYPE_NEW.OTHER_DEPOT
  },
  'Beteiligung/Firmenanteile': {
    asset_type: ASSET_MODAL_TYPE_NEW.EQUITY
  },
  'Eigene Wohnung/Haus': {
    asset_type: ASSET_MODAL_TYPE_NEW.IMMOBILIEN
  },
  'Immobilien': {
    asset_type: ASSET_MODAL_TYPE_NEW.IMMOBILIEN
  },
  'Lebensversicherung': {
    asset_type: ASSET_MODAL_TYPE_NEW.INSURANCE,
    sub_category: 2,
    company_name: 'Unbekannt (aus altem Profil)',
  },
  'Geschlossene Fonds': {
    asset_type: ASSET_MODAL_TYPE_NEW.EQUITY
  },
  'Darlehensforderung': {
    asset_type: ASSET_MODAL_TYPE_NEW.DEMANDS,
    sub_category: 1
  },
  'Grundbesitz': {
    asset_type: ASSET_MODAL_TYPE_NEW.IMMOBILIEN,
    estate_type: 9
  },
  'Ersparnisse': {
    asset_type: ASSET_MODAL_TYPE_NEW.CASH,
    sub_category: 5,
  },
  'Edelmetalle': {
    asset_type: ASSET_MODAL_TYPE_NEW.TANGIBLE,
    sub_category: 7
  },
  'Schmuck/Uhren': {
    asset_type: ASSET_MODAL_TYPE_NEW.TANGIBLE,
    sub_category: 1
  },
  'Kunstobjekte': {
    asset_type: ASSET_MODAL_TYPE_NEW.TANGIBLE,
    sub_category: 2
  },
  'Antiquitäten': {
    asset_type: ASSET_MODAL_TYPE_NEW.TANGIBLE,
    sub_category: 2
  },
  'Andere Sammlerstücke': {
    asset_type: ASSET_MODAL_TYPE_NEW.TANGIBLE,
    sub_category: 2
  },
  'Autos & Oldtimer': {
    asset_type: ASSET_MODAL_TYPE_NEW.TANGIBLE,
    sub_category: 3
  },
  'Booten und Yachten': {
    asset_type: ASSET_MODAL_TYPE_NEW.TANGIBLE,
    sub_category: 4
  },
  'Flugzeug': {
    asset_type: ASSET_MODAL_TYPE_NEW.TANGIBLE,
    sub_category: 5
  },
  'Weine/Spirituosen': {
    asset_type: ASSET_MODAL_TYPE_NEW.TANGIBLE,
    sub_category: 6
  },
  'Rohstoffe': {
    asset_type: ASSET_MODAL_TYPE_NEW.TANGIBLE,
    sub_category: 8
  },
  'Andere Vermögenswerte': {
    asset_type: ASSET_MODAL_TYPE_NEW.OTHER,
  },
  'Kreditkarten': {
    asset_type: ASSET_MODAL_TYPE_NEW.CREDIT_CARD,
  },
  'Hypothek': {
    asset_type: ASSET_MODAL_TYPE_NEW.MORTGAGE,
  },
  'Darlehen': {
    asset_type: ASSET_MODAL_TYPE_NEW.LOAN,
  },
  'Andere Verbindlichkeiten': {
    asset_type: ASSET_MODAL_TYPE_NEW.OTHER_LIABILITIES,
  }
};

const SyncPopup = (props) => {
  const {
    classes,
    customerId,
    open,
    title,
    description,
    noItemsDescription,
    itemSingleLabel,
    itemPluralLabel,
    sessionID,
    questionUID,
    handleClose,
    setSynced,
    syncIncompleteTitle,
    syncIncompleteDescription,
  } = props;

  const [showConfirmNotFullMapped, setShowConfirmNotFullMapped] = React.useState(false);
  const [modalLoading, setModalLoading] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const riskProfileData = useAssetsLiabilitiesFromRiskProfile(customerId, sessionID);

  const selectedItems = items.filter(i => !!i.selected);

  React.useEffect(() => {
    if(!riskProfileData.loading){
      const answers = _.get(riskProfileData.data, 'response.answers', []);

      const question = answers.find(q => getUID(q.question_uid) === questionUID);

      if(question){
        const allCategories = _.keyBy(getAssetsListData(questionUID).items, 'name');

        setItems(question.answer_data.answer.map(a => {
          const sub_category = a.asset_type || a.sub_category;
          const mapped = MAPPING[sub_category] || {};

          return {
            ...mapped,
            icon: _.get(allCategories, [sub_category, 'icon']),
            sub_category_display: sub_category,
            name: a.bezeichnung || a.name,
            value: a.wert || a.value,
            selected: true, // by default all selected
            full_mapped: mapped.hasOwnProperty('sub_category') || (
              mapped.asset_type === ASSET_MODAL_TYPE_NEW.IMMOBILIEN && mapped.hasOwnProperty('estate_type'))
          }
        }));
      }
    }
  }, [riskProfileData.updatedAt]);

  const sendData = async (items) => {
    setModalLoading(true);

    if(await setSynced(props.dispatch, items)){
      setShowConfirmNotFullMapped(false);
      handleClose();
    }

    setModalLoading(false);
  };

  const confirmNo = async () => {
    await sendData();
  };

  const confirmYes = async (event, force=false) => {
    if(!force && !_.every(selectedItems, 'full_mapped')){
      setShowConfirmNotFullMapped(true);
      return;
    }

    await sendData(selectedItems);
  };

  // Handler for when a checkbox state changes
  const handleCheckboxChange = (idx) => {
    setItems(prevState => prevState.map((item, index) =>
      idx === index ? { ...item, selected: !item.selected } : item
    ));
  };

  const hasItems = items.length > 0;

  return (
    <BaseDialog
      open={open}
      title={<><i className={'far fa-exclamation-circle'} />&nbsp;{showConfirmNotFullMapped ? syncIncompleteTitle : title}</>}
      data-id={'other-assets-and-liabilities-sync-popup'}
      handleClose={handleClose}
      modalLoading={riskProfileData.loading || modalLoading}
      customClasses={classes}
      maxWidth={showConfirmNotFullMapped ? 'md' : 'lg'}
      actions={
        <Grid container spacing={1} justify={"flex-end"}>
          {showConfirmNotFullMapped ? (
            <>
              <Grid item>
                <PrimaryButton
                  variant={'outlined'}
                  text={'Abbrechen'}
                  onButtonClick={handleClose}
                />
              </Grid>
              <Grid item>
                <PrimaryButton
                  text={'Weiter'}
                  onButtonClick={(e) => confirmYes(e, true)}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item>
                <PrimaryButton
                  variant={'outlined'}
                  text={'Abbrechen'}
                  onButtonClick={handleClose}
                />
              </Grid>
              <Grid item>
                <PrimaryButton
                  variant={hasItems ? 'outlined' : 'contained'}
                  text={hasItems ? 'Nicht mehr anzeigen' : 'OK'}
                  onButtonClick={confirmNo}
                />
              </Grid>
              {hasItems && (
                <Grid item>
                  <PrimaryButton
                    text={'Übernehmen'}
                    onButtonClick={confirmYes}
                    disabled={selectedItems.length === 0}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      }
    >
      <Grid container spacing={2} justify={'center'}>
        <Grid item xs={12}>
          {showConfirmNotFullMapped ? syncIncompleteDescription : hasItems ? description : noItemsDescription}
        </Grid>
        {hasItems && (
          <Grid item xs={12} md={showConfirmNotFullMapped ? 12 : 9}>
            <Grid container spacing={1}>
              {showConfirmNotFullMapped
                ? Object.entries(_.groupBy(selectedItems.filter(i => !i.full_mapped), 'asset_type')).map(([categoryId, assets]) =>
                  <GroupedByCategory key={categoryId} categoryId={categoryId} assets={assets} />
                )
                : <>
                  <Grid item xs={12}>
                    <Grid container spacing={1} alignItems={'center'}>
                      <Grid item className={classes.categoryName}>{itemPluralLabel}</Grid>
                      <Grid item className={classes.categoryItemsCount}>
                        ({items.length} {items.length === 1 ? itemSingleLabel : itemPluralLabel})
                      </Grid>
                    </Grid>
                  </Grid>
                  {items.map((item, idx) => (
                    <Grid item xs={12} key={`${itemPluralLabel}-${idx}`}>
                      <SelectAssetCard
                        item={item}
                        handleCheckboxChange={() => handleCheckboxChange(idx)}
                      />
                    </Grid>
                  ))}
                </>
              }
            </Grid>
          </Grid>
        )}
      </Grid>
    </BaseDialog>
  )
};

const GroupedByCategory = ({categoryId, assets}) => {
  let category, defaultSubCategory;
  if(categoryId == ASSET_MODAL_TYPE_NEW.OTHER_DEPOT){
    category = {...OTHER_FINANCIAL_CATEGORY};
    defaultSubCategory =  category.subCategories.find(c => c.id === ASSET_MODAL_TYPE_NEW.OTHER_DEPOT);
  } else {
    category = {...GROUPED_ASSET_AND_LIABILITY_CATEGORIES[categoryId]};
    defaultSubCategory = category.defaultSubcategory || (category.subCategories || []).find(c => c.isDefault) || {};
  }

  return (
    <Grid item xs={12}>
      <Grid container spacing={1}>
        <CategoryHeader category={category} withTotal={false} />
        {assets.map((asset, idx) => (
          <Grid item xs={12} key={`${category}-item-${idx}`}>
            <SelectAssetCard
              categoryIcon={category.reactIcon}
              item={{
                ...asset,
                icon: null,
                sub_category_display: defaultSubCategory.label || asset.sub_category_display
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  )
};

export default connect()(withStyles(styles)(SyncPopup));